<template>
    <tailwind-layout title="Blogs">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto flex justify-between items-center">
                <h1 class="font-semibold text-2xl leading-tight">Edit Blog Post</h1>
                <div class="flex items-center space-x-4">
                    <span class="isolate inline-flex rounded-md shadow-sm">
                        <inertia-link :href="route('blogs.index')"
                                      class="relative inline-flex items-center rounded-l-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                                      :class="blog.deleted_at ? 'rounded-r-md' : ''">
                            <font-awesome-icon :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                        </inertia-link>
                        <button v-if="!blog.deleted_at"
                                type="button"
                                class="relative -ml-px inline-flex items-center rounded-r-md px-3 py-2 text-sm font-semibold text-gray-900 bg-white hover:bg-gray-50 ring-1 ring-inset ring-gray-300 focus:z-10"
                                @click="showArchiveModal = true;">
                            <font-awesome-icon :icon="faBox" class="h-5 w-5 text-gray-900" aria-hidden="true" />
                            <span class="ml-2">Archive</span>
                        </button>
                    </span>
                </div>
            </div>
        </template>
        <trashed-message v-if="blog.deleted_at" class="mb-6" @restore="showRestoreModal = true;">
            This blog has been archived.
        </trashed-message>
        <div v-else class="overflow-hidden shadow-xl rounded-lg p-6 bg-white">
            <form @submit.prevent="update">
                <div class="space-y-12">
                    <div class="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
        
                        <div class="grid max-w-4xl grid-cols-1 gap-6 sm:grid-cols-6 md:col-span-2">
                            <div class="sm:col-span-full">
                                <label for="title" :class="form.errors.title ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium required-input">Title</label>
                                <div class="mt-1">
                                    <input id="title" type="text" v-model="form.title" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <p class="text-red-500 text-sm" v-text="form.errors.title"></p>
                            </div>

                            <div class="sm:col-span-full">
                                <label for="category" :class="form.errors.category ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium required-input">Category</label>
                                <div class="mt-1">
                                    <select id="category" v-model="form.blog_category_id" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md">
                                        <option value="">Choose category</option>
                                        <option v-for="category in categories" :key="category.id" :value="category.id">{{ category.name }}</option>
                                    </select>
                                </div>
                                <p class="text-red-500 text-sm" v-text="form.errors.category"></p>
                            </div>

                            <div class="sm:col-span-full">
                                <label for="description" :class="form.errors.description ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium required-input">Description</label>
                                <div class="mt-1">
                                    <TiptapEditor v-model="form.description" />
                                </div>
                                <p class="text-red-500 text-sm" v-text="form.errors.description"></p>
                            </div>
                        </div>

                        <div class="flex flex-col justify-start gap-6">
                            <div>
                                <label for="meta_title" :class="form.errors.meta_title ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium">Meta Title</label>
                                <div class="mt-1">
                                    <input id="meta_title" type="text" v-model="form.meta_title" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <p class="text-red-500 text-sm" v-text="form.errors.meta_title"></p>
                            </div>

                            <div>
                                <label for="meta_description" :class="form.errors.meta_description ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium">Meta Description</label>
                                <div class="mt-1">
                                    <textarea id="meta_description" v-model="form.meta_description" type="text" v-bind:class="(form.errors.meta_description)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md'"></textarea>
                                </div>
                                <p class="text-red-500 text-sm" v-text="form.errors.meta_description"></p>
                            </div>

                            <div>
                                <label for="image" :class="form.errors.image ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium">Image</label>
                                <div class="mt-2" v-show="!photoPreview && blog.image_url">
                                    <img :src="blog.image_url" class="w-[100px] h-[100px] mb-3">
                                </div>
                                <div class="mt-2" v-show="photoPreview">
                                    <span class="block w-[100px] h-[100px] mb-3"
                                        :style="'background-size: cover; background-repeat: no-repeat; background-position: center center; background-image: url(\'' + photoPreview + '\');'">
                                    </span>
                                </div>
                                <div class="mt-1">
                                    <input type="file" name="image" @input="form.image = $event.target.files[0]" ref="photo" @change="updatePhotoPreview" />
                                </div>
                                <p class="text-red-500 text-sm">Recommended 1000 X 1000 pixels minimum</p>
                                <p class="text-red-500 text-sm" v-text="form.errors.image"></p>
                            </div>
                        </div>

                    </div>
                </div>

                <div class="pt-5">
                    <div class="flex justify-end">
                        <inertia-link :href="route('blogs.index')" class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</inertia-link>
                        <form-button :form="form" class="ml-3">{{ $page.props.user.is_admin ? 'Update' : 'Resubmit' }}</form-button>
                    </div>
                </div>
            </form>
        </div>
        <div v-if="showArchiveModal">
            <TransitionRoot as="template" :show="showArchiveModal">
                <Dialog as="div" class="relative z-50" @close="showArchiveModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                                            <font-awesome-icon :icon="faBoxArchive" class="h-6 w-6 text-red-600" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" class="text-xl font-semibold leading-6 text-gray-900">Are you sure you want to archive this blog?</DialogTitle>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showArchiveModal = false">Cancel</button>
                                        <button type="button" class="primaryButton w-1/2 ml-1 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white hover:bg-black focus:outline-2 focus:outline-offset-2 focus:outline-transparent" @click="destroy()">Confirm</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>
        <div v-if="showRestoreModal">
            <TransitionRoot as="template" :show="showRestoreModal">
                <Dialog as="div" class="relative z-50" @close="showRestoreModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                            <font-awesome-icon :icon="faArrowsRotate" class="h-6 w-6 text-green-500" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Are you sure you want to restore this blog?</DialogTitle>
                                            <div class="mt-2">
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showRestoreModal = false">Cancel</button>
                                        <button type="button" class="primaryButton w-1/2 ml-1 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white hover:bg-black focus:outline-2 focus:outline-offset-2 focus:outline-transparent" @click="restore()">Confirm</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>
    </tailwind-layout>
</template>

<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import FormButton from '@/Shared/FormButton.vue'
import TrashedMessage from '@/Shared/TrashedMessage.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
    faArrowsRotate,
    faBox,
    faBoxArchive,
    faChevronLeft
} from '@fortawesome/pro-solid-svg-icons'
import {Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import TiptapEditor from '@/Shared/TiptapEditor.vue'

export default {
    props: {
        categories: Array,
        blog: Object,
    },
    components: {
        TailwindLayout,
        FormButton,
        TrashedMessage,
        FontAwesomeIcon,
        Dialog,
        DialogPanel,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        TiptapEditor
    },
    data() {
        return {
            form: this.$inertia.form({
                _method: 'PUT',
                title: this.blog.title,
                meta_title: this.blog.meta_title,
                blog_category_id: this.blog.blog_category_id,
                description: this.blog.description,
                meta_description: this.blog.meta_description,
                image: null,
            }),
            photoPreview: null,
            showArchiveModal: false,
            showRestoreModal : false,
            faChevronLeft,
            faBox,
            faArrowsRotate,
        }
    },
    methods: {
        update() {
            this.form.post(this.route('blogs.update', this.blog.id), {
                preserveScroll: true,
                forceFormData: true,
            });
        },
        destroy() {
            this.$inertia.delete(this.route('blogs.destroy', this.blog.id), {
                onFinish: () => {
                    this.showArchiveModal = false;
                }
            })
        },
        restore() {
            this.$inertia.put(this.route('blogs.restore', this.blog.id), {}, {
                onFinish: () => {
                    this.showRestoreModal = false;
                }
            })
        },
        updatePhotoPreview() {
            console.log('photoPreview');
            const photo = this.$refs.photo.files[0];

            if (! photo) return;

            const reader = new FileReader();

            reader.onload = (e) => {
                this.photoPreview = e.target.result;
                console.log('this.photoPreview: ', this.photoPreview);
            };

            reader.readAsDataURL(photo);
        },
    }
}
</script>
